import * as integration from './integration.actions';
import {AttributeMetadataModel, DataAccessRequestModel} from '../../model/integration-models';
import {IntegrationActionTypes} from './integration.actions';
import {Integration} from '../../../views/admin/application-settings/models/integration.model';
import {ApplicationProviderListModel} from '../../../views/admin/application-settings/models/application-provider-list.model';
import {InstitutionsVendorListModel} from '../../model/institutional-model';
import {ISchoolYearRolloverSettings} from '@app/core/model/rollover-model';
import {ICategory} from '@app/core/comp/filtering/filter';
import {ApplicationGalleryModel} from '@app/views/admin/application-settings/models/application-gallery.model';

export interface State {
    // default (base) integration for vendor
    defaultIntegration?: Integration;
    defaultSchema?: AttributeMetadataModel[];
    defaultCategories?: ICategory[];
    defaultCategoriesFailed?: Error;
    allPendingRequests?: DataAccessRequestModel[];
    pendingRequests?: DataAccessRequestModel[];
    providerApplications?: ApplicationProviderListModel[];
    deletingApplications?: ApplicationProviderListModel[];
    providerApplicationsFailed?: Error;
    vendorInstitutions?: InstitutionsVendorListModel[];
    vendorInstitutionsFailed?: Error;
    allVendorApplications?: ApplicationGalleryModel[];
    allVendorApplicationsFailed?: Error;
    schoolYearRolloverSettings?: ISchoolYearRolloverSettings;
    schoolYearRolloverSettingsFailed?: Error;
}


export function integrationReducer(state: any = {}, action: integration.Actions): State {

    switch (action. type) {
        case IntegrationActionTypes.INTEGRATION_GET_DEFAULT:
        case IntegrationActionTypes.INTEGRATION_GET_DEFAULT_FAILED:
            return Object.assign({}, state, {
                defaultIntegration: undefined
            });

        case IntegrationActionTypes.INTEGRATION_GET_DEFAULT_SUCCESS:
            return Object.assign({}, state, {
                defaultIntegration: action.payload.integration
            });

        case IntegrationActionTypes.GET_DEFAULT_SCHEMA:
        case IntegrationActionTypes.GET_DEFAULT_SCHEMA_FAILED:
            return Object.assign({}, state, {
                defaultSchema: undefined
            });

        case IntegrationActionTypes.GET_DEFAULT_SCHEMA_SUCCESS:
            return Object.assign({}, state, {
                defaultSchema: action.payload.attributeMetadata
            });

        case IntegrationActionTypes.GET_DEFAULT_CATEGORIES:
            return Object.assign({}, state, {
                defaultCategories: undefined,
                defaultCategoriesFailed: null
            });

        case IntegrationActionTypes.GET_DEFAULT_CATEGORIES_SUCCESS:
            return Object.assign({}, state, {
                defaultCategories: action.payload,
                defaultCategoriesFailed: null
            });

        case IntegrationActionTypes.GET_DEFAULT_CATEGORIES_FAILED:
            return Object.assign({}, state, {
                defaultCategories: undefined,
                defaultCategoriesFailed: action.payload
            });

        case IntegrationActionTypes.GET_PENDING_REQUESTS_FAILED:
            return Object.assign({}, state, {
                allPendingRequests: <DataAccessRequestModel[]>[],
                pendingRequests: <DataAccessRequestModel[]>[]
            });

        case IntegrationActionTypes.GET_PENDING_REQUESTS_SUCCESS:
            return Object.assign({}, state, {
                allPendingRequests: action.payload.allPendingRequests,
                pendingRequests: action.payload.pendingRequests,
            });

        case IntegrationActionTypes.GET_PROVIDER_APPLICATIONS_SUCCESS:
            return Object.assign({}, state, {
                providerApplications: action.payload.applications,
                providerApplicationsFailed: null
            });

        case IntegrationActionTypes.GET_PROVIDER_APPLICATIONS_FAILED:
            return Object.assign({}, state, {
                providerApplications: <ApplicationProviderListModel[]>[],
                providerApplicationsFailed: action.payload
            });

        case IntegrationActionTypes.SET_DELETING_APPLICATIONS:
            return Object.assign({}, state, {
                deletingApplications: action.payload
            });

        case IntegrationActionTypes.GET_VENDOR_INSTITUTIONS_SUCCESS:
            return Object.assign({}, state, {
                vendorInstitutions: action.payload.institutions,
                vendorInstitutionsFailed: null
            });

        case IntegrationActionTypes.GET_VENDOR_INSTITUTIONS_FAILED:
            return Object.assign({}, state, {
                vendorInstitutions: <InstitutionsVendorListModel[]>[],
                vendorInstitutionsFailed: action.payload
            });

        case IntegrationActionTypes.GET_ALL_VENDOR_APPLICATIONS_SUCCESS:
            return Object.assign({}, state, {
                allVendorApplications: action.payload.allVendorApplications,
                allVendorApplicationsFailed: null
            });

        case IntegrationActionTypes.GET_ALL_VENDOR_APPLICATIONS_FAILED:
            return Object.assign({}, state, {
                allVendorApplications: <ApplicationGalleryModel[]>[],
                allVendorApplicationsFailed: action.payload
            });

        case IntegrationActionTypes.SET_SCHOOL_YEAR_DATA_ROLLOVER_SETTINGS_SUCCESS:
            const result: ISchoolYearRolloverSettings = {};

            if (action.payload) {
                action.payload.forEach((setting) => {
                    result[setting.settingsType] = setting;
                });
            }

            return Object.assign({}, state, {
                schoolYearRolloverSettings: result,
                schoolYearRolloverSettingsFailed: null
            });

        case IntegrationActionTypes.GET_SCHOOL_YEAR_DATA_ROLLOVER_SETTINGS_FAILED:
            return Object.assign({}, state, {
                schoolYearRolloverSettings: null,
                schoolYearRolloverSettingsFailed: action.payload
            });

        case IntegrationActionTypes.INTEGRATION_CLEAR_STATE:
            return Object.assign({}, state, {
                defaultIntegration: undefined,
                allPendingRequests: undefined,
                pendingRequests: undefined,
                providerApplications: undefined,
                providerApplicationsFailed: undefined,
                vendorInstitutions: undefined,
                vendorInstitutionsFailed: undefined
            });

        case IntegrationActionTypes.INTEGRATION_CLEAR_SESSION_STORED_DATA:
            return Object.assign({}, state, {
                allVendorApplications: undefined,
                allVendorApplicationsFailed: undefined,
                defaultSchema: undefined,
                deletingApplications: undefined,
                defaultCategories: undefined,
                defaultCategoriesFailed: undefined,
                schoolYearRolloverSettings: undefined,
                schoolYearRolloverSettingsFailed: undefined
            });

        default:
            return state;

    }
}

export const getDefaultIntegration = (state: State) => state.defaultIntegration;
export const getDefaultSchema = (state: State) => state.defaultSchema;
export const getDefaultCategories = (state: State) => state.defaultCategories;
export const getDefaultCategoriesFailed = (state: State) => state.defaultCategoriesFailed;
export const getAllPendingRequests = (state: State) => state.allPendingRequests;
export const getPendingRequests = (state: State) => state.pendingRequests;
export const getProviderApplications = (state: State) => state.providerApplications;
export const getProviderApplicationsFailed = (state: State) => state.providerApplicationsFailed;
export const getDeletingApplications = (state: State) => state.deletingApplications;
export const getVendorInstitutions = (state: State) => state.vendorInstitutions;
export const getVendorInstitutionsFailed = (state: State) => state.vendorInstitutionsFailed;
export const getAllVendorApplications = (state: State) => state.allVendorApplications;
export const getAllVendorApplicationsFailed = (state: State) => state.allVendorApplicationsFailed;
export const getSchoolYearRolloverSettings = (state: State) => state.schoolYearRolloverSettings;
export const getSchoolYearRolloverSettingsFailed = (state: State) => state.schoolYearRolloverSettingsFailed;
